// Base
import { useState, ChangeEvent, useEffect } from 'react'
import { useRouter } from 'next/router'

// Redux
import { useAppDispatch } from '@redux/hooks'
import { showMessage } from '@redux/slices/utilitiesSlice'

// Components
import { Flex, Button, Text, Box } from 'yard-utilities/components'
import Title from './title/Title'
import { Input } from '@components/v25/ft1/input/Input'
import LocationPopup from '@components/v25/membership/location/location-popup/LocationPopup'

// Helpers
import validateEmail from '@helpers/validateEmail'
import { trackLead } from '@helpers/trackers'

// Assets
import { LocationIcon } from 'icons/v21/Icons'

// Data
import cities from '@data/v21/cities.json'
import { locationTags } from '@sections/v21/location/LocationWrapper'

// Styles
import styles from './CreateAccount.module.sass'

interface CityData {
  id: number
  name: string
}

interface CreateAccountProps {
  redirectURL: string
}

const CreateAccount = ({ redirectURL }: CreateAccountProps) => {
  const [email, setEmail] = useState('')
  const [showSelection, setShowSelections] = useState(false)
  const [location, setLocation] = useState('')
  const [selection, setSelection] = useState<CityData>({
    id: 0,
    name: ''
  })
  const [displayPopup, setDisplayPopup] = useState(false)

  const router = useRouter()

  const dispatch = useAppDispatch()

  const handleSubmit = async (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (validateEmail(email)) {
      setShowSelections(true)

      if (location && selection.id) {
        trackLead(email)

        router.push({
          pathname: redirectURL,
          query: {
            ...router.query,
            city: selection.id,
            location: selection.name,
            email
          }
        })
      }
    } else {
      dispatch(
        showMessage({
          type: 'danger',
          text: 'Incorrect email. Please enter a valid email'
        })
      )
    }
  }

  useEffect(() => {
    if (router.query.email) {
      setShowSelections(true)
      setEmail(router.query.email as string)
    }

    if (router.query.city && router.query.location) {
      setSelection({
        id: parseInt(router.query.city as string),
        name: router.query.location as string
      })
      setLocation(router.query.location as string)
    }
  }, [router.query])

  return (
    <div>
      {displayPopup && (
        <LocationPopup
          location={location}
          setLocation={setLocation}
          setSelection={setSelection}
          setDisplayPopup={setDisplayPopup}
          cities={cities}
          popularCities={locationTags}
        />
      )}
      <Text
        tagName="h4"
        variant="title-3"
        spacing={{ sm: 32 }}
        align="center"
        transform="capitalize"
        className={styles.title}
      >
        Create Your Account
      </Text>

      <form onSubmit={handleSubmit}>
        <Flex flexDirection="column" rowGap={{ sm: 32 }}>
          <div>
            <Title index={1}>Enter Email Address</Title>
            <Input
              placeholder="Enter your email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value.toLowerCase())
              }}
            />
          </div>

          {showSelection && (
            <div>
              <Title index={2}>Set Home Airport</Title>

              <div className={styles.selection}>
                <Input
                  startIcon={<LocationIcon />}
                  placeholder="Type your location"
                  id="location"
                  name="location"
                  value={location}
                  readOnly
                  onClick={() => {
                    setDisplayPopup(true)
                  }}
                />
              </div>
            </div>
          )}

          <Button variant="yellow" transform="uppercase">
            {showSelection ? 'Save details' : 'Next, set my home airport'}
          </Button>
        </Flex>
      </form>
    </div>
  )
}

export default CreateAccount
